// CareUI registry source: ohcnetwork/careui (4e143863).
/**
 * @name select
 * @description Displays a list of options for the user to pick from—triggered by a button.
 * @dependencies @base-ui/react lucide-react
 * @type registry:ui
 */
import { Select as SelectPrimitive } from "@base-ui/react/select";
import * as React from "react";

import { cn } from "@/lib/utils";
import { CheckIcon, ChevronDownIcon, ChevronUpIcon } from "lucide-react";

interface SelectProps<Value extends string> extends Omit<
  SelectPrimitive.Root.Props<Value>,
  "onValueChange" | "value" | "defaultValue"
> {
  value?: Value | "" | null;
  defaultValue?: Value | "" | null;
  onValueChange?: (
    value: Value,
    details: SelectPrimitive.Root.ChangeEventDetails,
  ) => void;
}

// Existing CARE forms declare options as children. Supply their labels to Base UI
// before the portalled popup mounts so the trigger never displays an internal ID.
function collectItems(
  children: React.ReactNode,
): { value: string; label: React.ReactNode }[] {
  return React.Children.toArray(children).flatMap((child) => {
    if (
      !React.isValidElement<{ children?: React.ReactNode; value?: string }>(
        child,
      )
    )
      return [];
    if (child.type === SelectItem && child.props.value !== undefined) {
      return [{ value: child.props.value, label: child.props.children }];
    }
    return collectItems(child.props.children);
  });
}

function Select<Value extends string = string>({
  children,
  items,
  onValueChange,
  value,
  defaultValue,
  ...props
}: SelectProps<Value>) {
  return (
    <SelectPrimitive.Root<Value>
      {...props}
      value={value === "" ? null : value}
      defaultValue={defaultValue === "" ? null : defaultValue}
      items={items ?? collectItems(children)}
      onValueChange={(next, details) => {
        if (next !== null && next !== "") onValueChange?.(next, details);
      }}
    >
      {children}
    </SelectPrimitive.Root>
  );
}

function SelectGroup({ className, ...props }: SelectPrimitive.Group.Props) {
  return (
    <SelectPrimitive.Group
      data-slot="select-group"
      className={cn("scroll-my-1 p-1", className)}
      {...props}
    />
  );
}

function SelectValue({ className, ...props }: SelectPrimitive.Value.Props) {
  return (
    <SelectPrimitive.Value
      data-slot="select-value"
      className={cn("flex flex-1 text-left", className)}
      {...props}
    />
  );
}

function SelectTrigger({
  className,
  size = "default",
  children,
  ...props
}: SelectPrimitive.Trigger.Props & {
  size?: "sm" | "default";
}) {
  return (
    <SelectPrimitive.Trigger
      data-slot="select-trigger"
      data-size={size}
      className={cn(
        "border-input data-placeholder:text-placeholder-foreground dark:bg-input/30 dark:hover:bg-input/50 focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive dark:aria-invalid:border-destructive/50 flex h-(--select-trigger-height) w-fit items-center justify-between gap-1.5 rounded-md border bg-transparent py-2 pr-(--select-trigger-pr) pl-(--select-trigger-pl) text-sm whitespace-nowrap shadow-xs transition-[color,box-shadow] outline-none focus-visible:ring-3 disabled:cursor-not-allowed disabled:opacity-50 aria-invalid:ring-3 *:data-[slot=select-value]:line-clamp-1 *:data-[slot=select-value]:flex *:data-[slot=select-value]:items-center *:data-[slot=select-value]:gap-1.5 [--select-trigger-pr:--spacing(2.5)] [--select-trigger-pl:--spacing(3)] md:[--select-trigger-pr:--spacing(2)] md:[--select-trigger-pl:--spacing(2.5)] [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
        size === "sm"
          ? "[--select-trigger-height:--spacing(10)] md:[--select-trigger-height:--spacing(9)]"
          : "[--select-trigger-height:--spacing(12)] md:[--select-trigger-height:--spacing(10)]",
        className,
      )}
      {...props}
    >
      {children}
      <SelectPrimitive.Icon
        // The SVG supplies its paths; suppress Base UI's default text glyph.
        children={null}
        render={
          <ChevronDownIcon className="text-muted-foreground pointer-events-none size-4" />
        }
      />
    </SelectPrimitive.Trigger>
  );
}

function SelectContent({
  className,
  children,
  side = "bottom",
  sideOffset = 4,
  align = "center",
  alignOffset = 0,
  position = "popper",
  alignItemWithTrigger = position === "item-aligned",
  collisionPadding,
  collisionAvoidance,
  avoidCollisions = true,
  ...props
}: SelectPrimitive.Popup.Props &
  Pick<
    SelectPrimitive.Positioner.Props,
    | "align"
    | "alignOffset"
    | "side"
    | "sideOffset"
    | "alignItemWithTrigger"
    | "collisionPadding"
    | "collisionAvoidance"
  > & { position?: "popper" | "item-aligned"; avoidCollisions?: boolean }) {
  return (
    <SelectPrimitive.Portal>
      <SelectPrimitive.Positioner
        side={side}
        sideOffset={sideOffset}
        align={align}
        alignOffset={alignOffset}
        alignItemWithTrigger={alignItemWithTrigger}
        collisionPadding={collisionPadding}
        collisionAvoidance={
          avoidCollisions
            ? collisionAvoidance
            : { side: "none", align: "none", fallbackAxisSide: "none" }
        }
        className="isolate z-50"
      >
        <SelectPrimitive.Popup
          data-slot="select-content"
          data-align-trigger={alignItemWithTrigger}
          className={cn(
            "bg-popover text-popover-foreground data-open:animate-in data-closed:animate-out data-closed:fade-out-0 data-open:fade-in-0 data-closed:zoom-out-95 data-open:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 ring-foreground/10 relative isolate z-50 max-h-(--available-height) w-max min-w-[max(9rem,var(--anchor-width))] max-w-(--available-width) origin-(--transform-origin) overflow-x-hidden overflow-y-auto rounded-md shadow-md ring-1 duration-100 data-[align-trigger=true]:animate-none",
            className,
          )}
          {...props}
        >
          <SelectScrollUpButton />
          <SelectPrimitive.List>{children}</SelectPrimitive.List>
          <SelectScrollDownButton />
        </SelectPrimitive.Popup>
      </SelectPrimitive.Positioner>
    </SelectPrimitive.Portal>
  );
}

function SelectLabel({
  className,
  ...props
}: SelectPrimitive.GroupLabel.Props) {
  return (
    <SelectPrimitive.GroupLabel
      data-slot="select-label"
      className={cn("text-muted-foreground px-2 py-1.5 text-xs", className)}
      {...props}
    />
  );
}

function SelectItem({
  textValue,
  className,
  children,
  ...props
}: SelectPrimitive.Item.Props & { textValue?: string }) {
  return (
    <SelectPrimitive.Item
      data-slot="select-item"
      label={textValue}
      className={cn(
        "focus:bg-accent focus:text-accent-foreground not-data-[variant=destructive]:focus:**:text-accent-foreground relative flex w-full cursor-default items-center gap-2 rounded-sm py-(--select-item-py) pr-8 pl-(--select-item-pl) text-sm outline-hidden select-none data-disabled:pointer-events-none data-disabled:opacity-50 [--select-item-py:--spacing(2.5)] [--select-item-pl:--spacing(2.5)] md:[--select-item-py:--spacing(1.5)] md:[--select-item-pl:--spacing(2)] [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4 *:[span]:last:flex *:[span]:last:items-center *:[span]:last:gap-2",
        className,
      )}
      {...props}
    >
      <SelectPrimitive.ItemText className="flex min-w-0 flex-1 gap-2 whitespace-normal wrap-anywhere">
        {children}
      </SelectPrimitive.ItemText>
      <SelectPrimitive.ItemIndicator
        render={
          <span className="pointer-events-none absolute right-2 flex size-4 items-center justify-center" />
        }
      >
        <CheckIcon className="pointer-events-none" />
      </SelectPrimitive.ItemIndicator>
    </SelectPrimitive.Item>
  );
}

function SelectSeparator({
  className,
  ...props
}: SelectPrimitive.Separator.Props) {
  return (
    <SelectPrimitive.Separator
      data-slot="select-separator"
      className={cn("bg-border pointer-events-none -mx-1 my-1 h-px", className)}
      {...props}
    />
  );
}

function SelectScrollUpButton({
  className,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.ScrollUpArrow>) {
  return (
    <SelectPrimitive.ScrollUpArrow
      data-slot="select-scroll-up-button"
      className={cn(
        "bg-popover top-0 z-10 flex w-full cursor-default items-center justify-center py-1 [&_svg:not([class*='size-'])]:size-4",
        className,
      )}
      {...props}
    >
      <ChevronUpIcon />
    </SelectPrimitive.ScrollUpArrow>
  );
}

function SelectScrollDownButton({
  className,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.ScrollDownArrow>) {
  return (
    <SelectPrimitive.ScrollDownArrow
      data-slot="select-scroll-down-button"
      className={cn(
        "bg-popover bottom-0 z-10 flex w-full cursor-default items-center justify-center py-1 [&_svg:not([class*='size-'])]:size-4",
        className,
      )}
      {...props}
    >
      <ChevronDownIcon />
    </SelectPrimitive.ScrollDownArrow>
  );
}

export {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectScrollDownButton,
  SelectScrollUpButton,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
};
